<template>
  <slot :open="open" />
  <TirPmDrawer
    class="task-drawer-block"
    :visible="visible"
    size="51.875rem"
    :position="TirPmDrawerPositionEnum.Right"
    @update:visible="onUpdateVisible"
    :panelClass="panelClass"
    :overlayClass="panelClass"
  >
    <template #content>
      <TirPmNotificationGroup group="task-drawer" position="top-right" :zIndex="20000" />
      <div v-if="isInitLoading" class="task-drawer__skeleton">
        <TaskDrawerSkeleton />
      </div>
      <TirPmScrollbar v-else style="max-height: 100vh">
        <div class="task-drawer">
          <div class="task-drawer__header">
            <div class="task-drawer__header-breadcrumbs-row">
              <TirPmButton
                v-if="showBackButton"
                :variant="TirPmButtonVariantEnum.LinkState"
                :size="TirPmButtonSizeEnum.Regular"
                @click="emit('onBack')"
              >
                <template #leftSlot>
                  <ArrowUturnLeftIcon />
                </template>
              </TirPmButton>
              <TaskLocationDropdown
                v-if="isTaskLocation && items.length > 0 && !isTaskSaved"
                :items="items"
                v-model="taskLocation"
                @update:modelValue="onChangeTaskLocation"
              ></TaskLocationDropdown>

              <MenuBreadcrumbs
                v-else
                no-route
                :settings="breadcrumbsSettings"
                :color="MenuBreadcrumbsColorEnum.OnlyGray"
              />

              <div class="task-drawer__header-buttons">
                <TirPmTooltipElement :position="TirPmTooltipElementPlacementEnum.Left">
                  <RouterLink
                    v-if="!hideDetailButton && isSetQueueId && isEditMode"
                    :to="taskDetailRoute"
                  >
                    <TirPmButton
                      :variant="TirPmButtonVariantEnum.LinkState"
                      :size="TirPmButtonSizeEnum.Regular"
                      @mousedown.prevent
                    >
                      <template #leftSlot>
                        <ArrowsPointingOutIcon />
                      </template>
                    </TirPmButton>
                  </RouterLink>
                  <template #content>Развернуть на весь экран</template>
                </TirPmTooltipElement>

                <TirPmTooltipElement :position="TirPmTooltipElementPlacementEnum.Left">
                  <TaskMorePopover
                    v-if="isEditMode"
                    showAsDrawer
                    :queueId="editTaskInfo.QueueTaskId"
                    :paramRoute="paramRoute"
                    :subtasks="subtasks"
                    :userAccess="userAccess"
                    :task="editTaskInfo"
                    :task-id="editTaskInfo.Id"
                    :task-name="editTaskInfo.Name"
                    :project-id="editTaskInfo.ProjectId"
                    :parent-task-id="editTaskInfo.Parent?.Id"
                    :search-tasks-function="searchTasksChangeParent"
                    :is-loading-unattach-button="isLoadingUnattachParent"
                    :editing="editing"
                    :show-remove="isProjectAdmin"
                    @attach-parent-task="setParentTask($event, false)"
                    @change-parent-task="setParentTask($event, true)"
                    @unattach-parent-task="unAttachParentTask"
                    @copy-task-link="copyNameAndLinkTask"
                    @showSettingsTask="showSettingsTask"
                    @successRemove="onSuccessRemove(editTaskInfo.Id)"
                    @transferTask="closeDrawer"
                  >
                    <TirPmButton
                      :variant="TirPmButtonVariantEnum.LinkState"
                      :size="TirPmButtonSizeEnum.Regular"
                    >
                      <template #leftSlot>
                        <EllipsisHorizontalIcon />
                      </template>
                    </TirPmButton>
                  </TaskMorePopover>
                  <template #content>Доп. действия</template>
                </TirPmTooltipElement>
                <TirPmButton
                  v-if="showCloseButton"
                  :variant="TirPmButtonVariantEnum.LinkState"
                  :size="TirPmButtonSizeEnum.Regular"
                  @click="onUpdateVisible(false)"
                >
                  <template #leftSlot>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      class="task-drawer__subheader-fill-svg-icon"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L12 10.9393L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L13.0607 12L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L12 13.0607L6.53033 18.5303C6.23744 18.8232 5.76256 18.8232 5.46967 18.5303C5.17678 18.2374 5.17678 17.7626 5.46967 17.4697L10.9393 12L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z"
                      />
                    </svg>
                  </template>
                </TirPmButton>
              </div>
            </div>
            <TaskParentView
              class="task-drawer__header-parent-task"
              v-if="formData?.Parent?.Id"
              in-drawer
              :parent-task="formData.Parent"
              :is-loading-unattach="isLoadingUnattachParent"
              :show-delete-button="isEditMode"
              @unattach="unAttachParent"
            />
          </div>
          <div class="task-drawer__subheader">
            <div class="task-drawer__subheader-left">
              <TirPmButton
                v-if="!isEditMode"
                :variant="TirPmButtonVariantEnum.PrimaryState"
                :size="TirPmButtonSizeEnum.Small"
                :is-disabled="(!isChanged && !copyMode) || !formData.Name || !isSetQueueId"
                :is-loading="isLoadingCreateButton"
                @click="onClickActionButton"
              >
                Создать
              </TirPmButton>
              <TaskImportButton
                v-if="!isEditMode && !copyMode && isEnableWeeekImport"
                :model-value="activeImportService"
                @update:model-value="updateActiveImportService"
              />
              <div
                v-if="editTaskInfo?.Id"
                :class="['task-drawer__author', { 'is-have-id': !!editTaskInfo?.Id }]"
                @click="copyId"
              >
                <TirPmIconView
                  :size="24"
                  isRounded
                  :isBordered="false"
                  :colorCode="taskOwner.AvatarColor"
                  :imageLoadUrl="taskOwner.ImageUrl"
                >
                  <template #mainSlot>
                    {{ getUserInitials(taskOwner) }}
                  </template>
                </TirPmIconView>
                <span class="task-drawer__author-id" v-if="editTaskInfo?.Id">
                  {{ editTaskInfo.Id }}
                </span>
              </div>
              <TirPmTooltipElement>
                <TirPmButton
                  v-if="editTaskInfo?.Id"
                  class="task-drawer__copy-button"
                  :size="TirPmButtonSizeEnum.Small"
                  :variant="TirPmButtonVariantEnum.LinkState"
                  @click="copyTaskLink"
                >
                  <template #leftSlot>
                    <LinkIcon />
                  </template>
                </TirPmButton>
                <template #content>Скопировать ссылку</template>
              </TirPmTooltipElement>
              <WeeekTag v-if="isVisibleWeeekTag" :href="weeekTaskUrl" />
              <div
                class="task-drawer__subheader-description"
                v-if="!isVisibleWeeekTag || isEditMode"
              >
                {{ title }}
              </div>
            </div>
          </div>
          <TaskImportForm
            v-if="activeImportService !== undefined && !isEditMode"
            :project-id="projectId"
            :queue-id="queueId"
            :is-reimport="!!formData.WeeekTask"
            @import="setImportTask"
          />
          <div class="task-drawer__input-wrap">
            <TaskInputName
              :model-value="formData.Name"
              placeholder="Введите название задачи"
              :error-message="errors?.Name"
              v-model:mode="inputMode"
              auto-focus
              @update:model-value="updateTaskName"
              @copy-name="copyName"
              @copy-id="copyId"
              :editing="editing"
              :is-disabled="importDisableFields"
              :is-import-mode="activeImportService !== undefined"
            />

            <TirPmPlate
              :isShow="isShowTextChangeTarget"
              variant="default"
              isRounded
              class="template-import-form_plate"
            >
              <template #leftAddon>
                <TirPmStatusBudge statusIconType="info" statusColor="neutral-invert" />
              </template>
              <template #subtitleSlot
                >Чтобы увидеть все поля, выберите расположение тикета</template
              >
              <template #rightAddon>
                <div class="template-import-form_plate-button">
                  <TirPmButton
                    :variant="TirPmButtonVariantEnum.SecondaryState"
                    :size="TirPmButtonSizeEnum.Small"
                    @click="isShowTextChangeTarget = false"
                    >Понятно</TirPmButton
                  >
                </div>
              </template>
            </TirPmPlate>
          </div>

          <div class="task-drawer__tags" v-if="!isTaskLocation || isDefineQueueId || isSetQueueId">
            <TaskTagsSelect
              :isNamespaceAdmin="isNamespaceAdmin"
              :isProjectAdmin="isProjectAdmin"
              :model-value="formData.Tags"
              :options="dictionaries.Tags"
              @update:model-value="saveTaskTags"
              :editing="editing"
            />
          </div>

          <div
            class="task-drawer__import-files-alert"
            v-if="
              activeImportService !== undefined && formData.FilesToImport?.length && !isEditMode
            "
          >
            <TaskImportFilesAlert :import-service="activeImportService" :task-url="weeekTaskUrl" />
          </div>

          <div
            class="task-drawer__grid-row"
            v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
          >
            <TaskStatusDropdown
              :model-value="formData.StatusId"
              :options="dictionaries.Statuses"
              :editing="!statusDisabled"
              @update:model-value="saveTaskStatus"
            />
            <TaskTypeDropdown
              v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
              :model-value="formData.TypeId"
              :options="dictionaries.Types"
              @update:model-value="saveTaskType"
              :editing="editing"
            />
            <TaskTimeTrackerPopover
              v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
              ref="$timeTracker"
              :is-disabled="isTimeTrackerDisabled && !isTaskSaved"
              :records="timerTrackerRecords"
              :timer-start-date="timerStartDate"
              :is-loading="isLoadingTimerButton"
              :editing="editing"
              :isProjectAdmin="isProjectAdmin"
              @on-start="startTimer"
              @on-stop="stopTimer"
              @on-create-record="createTimerRecord"
              @on-delete-record="deleteTimerRecord"
              @on-edit-record="editTimerRecord"
            />
          </div>
          <div class="task-drawer__divider" />
          <div class="task-drawer__priority-difficulty">
            <TaskPriorityDropdown
              v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
              :model-value="formData.PriorityId"
              :options="dictionaries.Priorities"
              @update:model-value="saveTaskPriority"
              :editing="editing"
            />
            <TaskDifficultyDropdown
              v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
              :model-value="formData.DifficultyId"
              :options="dictionaries.Difficulties"
              @update:model-value="saveTaskDifficulty"
              :editing="editing"
            />
            <TaskDeadline
              :model-value="formData.DeadlineDate"
              @update:model-value="saveDeadline"
              v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
              :editing="editing"
            />
          </div>
          <div
            class="task-drawer__employees"
            v-if="!isTaskLocation || isDefineQueueId || isSetQueueId"
          >
            <TaskEmployeeMultiselect
              :model-value="formData.PerformerIds"
              :employees="multiselectPerformers"
              label="Исполнители"
              @update:model-value="saveTaskPerformers"
              :editing="editing"
            />
            <TaskEmployeeMultiselect
              :model-value="formData.ObserverIds"
              :employees="multiselectObservers"
              label="Наблюдатели"
              @update:model-value="saveTaskObservers"
              :editing="editing"
            />
            <TaskFilesProgress
              v-if="visibleFiles.length && isEditMode"
              :files="visibleFiles"
              :is-loading="isLoadingAbortFiles"
              @cancelUpload="abortFiles"
            />
          </div>
          <div class="task-drawer__description">
            <TirPmTextEditorFile
              v-if="descriptionIsEditMode && editing"
              class="task-drawer__editor"
              v-model="description"
              :all-files="editorFiles"
              label="Описание"
              :fileUploadCallbak="attachFileFromDescription"
              :is-hide-main-controls="!isEditMode"
              @save="saveDescription"
              @cancel="onClickCancelSaveDescription"
              @on-click-delete-file="removeFile"
            />
            <TirPmTextEditorFilePreview
              v-else
              label="Описание"
              :content="formData.Description"
              :all-files="editorFiles"
              :is-expand="isExpandDescription"
              @to-edit="descriptionIsEditMode = true"
              @change-expand="isExpandDescription = !isExpandDescription"
              @on-click-delete-file="removeFile"
              :editing="editing"
            />
          </div>
          <div class="task-drawer__subtasks" :style="subtasksElementStyle">
            <TaskRelations
              v-if="editTaskInfo"
              in-drawer
              :userAccess="userAccess"
              :editTaskInfo="editTaskInfo"
              :taskId="editTaskInfo.Id"
              :projectId="projectId"
              :queueId="editTaskInfo.QueueTaskId"
              :subtasks="subtasks"
              :queues="editTaskInfo.QueueToTaskRelationList"
              :tasksSearchFunction="searchTasksToAttach"
              :isEdit="isEditMode && editing"
              @attachSubtask="onAttachSubtask"
              @removeSubtask="onRemoveSubtask"
            />
          </div>
        </div>
      </TirPmScrollbar>
    </template>
  </TirPmDrawer>

  <TaskDialogModal ref="$dialogModal" />
</template>

<script lang="ts" setup>
import { computed, CSSProperties, nextTick, provide, ref, watch } from 'vue';
import { useFormValidator, FormValidators } from 'tir-pm-form-validation';
import {
  MenuBreadcrumbs,
  SettingsBreadcrumbsModel,
  MenuBreadcrumbsColorEnum,
} from '@/components/menu-breadcrumbs';
import {
  TirPmDrawer,
  TirPmDrawerPositionEnum,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmScrollbar,
  TirPmIconView,
  TirPmPlate,
  TirPmStatusBudge,
  TirPmTooltipElementPlacementEnum,
  TirPmTooltipElement,
} from 'tir-components';
import {
  ArrowsPointingOutIcon,
  ArrowUturnLeftIcon,
  EllipsisHorizontalIcon,
  LinkIcon,
} from 'tir-style-system/icons/outline';
import { AdapterService } from './adapter';
import {
  TaskStatusDropdown,
  TaskPriorityDropdown,
  TaskTypeDropdown,
  TaskDifficultyDropdown,
  TaskTagsSelect,
  TaskEmployeeMultiselect,
  TaskInputName,
  TaskDeadline,
  TaskNoSaveDataModal,
  TaskTimeTrackerPopover,
  TaskInputNameModeEnum,
  TaskMorePopover,
  TaskParentView,
  TaskRelations,
  TaskDrawerFormDataModel,
  TaskImportFilesAlert,
} from '../../components';
import {
  TaskFileModel,
  TaskImportDataModel,
  TaskImportFileModel,
  UserModel,
  WeeekTaskModel,
} from '../../models';
import {
  TaskFormDataModel,
  TaskDrawerSettingsModel,
  TaskModel,
  CreateTaskModel,
  TaskUpdateNameModel,
  TaskUpdateStatusModel,
  TaskUpdatePriorityModel,
  TaskUpdateTypeModel,
  TaskUpdateDifficultyModel,
  TaskUpdateObserversModel,
  TaskUpdatePerformersModel,
  TaskUpdateTagsModel,
  TaskUpdateDescriptionModel,
  TaskUpdateDateEndModel,
  ResponseModel,
  GetUserRoleByTaskIdsRequsetModel,
  GetUserRoleByTaskIdsItemModel,
} from './models';
import { TaskModel as ListTaskModel } from '@/components/tasks-kanban/models';
import { TaskDrawerMode } from './types';
import tirPmDateTransform from './utils/tir-pm-date-transform';
import { CurrentUser, CurrentUserModel, UserRoleEnum } from 'tir-pm-user-core';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import {
  ITextEditorFile,
  TirPmTextEditorFile,
  TirPmTextEditorFilePreview,
} from 'tir-pm-texteditor-file';
import {
  TirPmNotificationEventBus,
  TirPmNotificationGroup,
  TirPmNotificationModel,
} from 'tir-pm-notification';
import { copyToClipboard } from '../../utils';
import { useTaskSubtasks, useTaskFiles, useTaskDictionaries, useTaskTimer } from './composables';
import { useTaskFormDataTransfer } from '../../composables';
import { TaskDrawerSettingsTypeGuard } from './type-guards';
import { mapToEditorFile, mapToTransferFormData, mapTaskToFormData } from './mappers';
import { NamespaceModel, QueueTasksModel } from '@/components/sidebar/models';
import TaskLocationDropdown from '@/components/task-location-dropdown/task-location-dropdown.vue';
import { TasksKanbanColumnModel } from '@/components/tasks-kanban/models';
import { AccessAdapterService } from '../task-detail/adapter/AccessAdapterService';
import { MiddlewareAdapterService } from '@/router/middleware';
import DifficultyLevelTaskModel from '@/pages/monitorings/models/DifficultyLevelTaskModel';
import { TaskParameterModel } from '@/components/task-parameter';
import { TaskImportButton, TaskImportForm } from '../task-import';
import { TaskImportServiceEnum } from '@/enums';
import { TaskDialogEnum, TaskDialogModal } from '../task-dialog-modal';
import { ComponentInstance } from 'tir-pm-toolkit';
import { WeeekTag } from '@/components/weeek-tag';
import { TaskFilesProgress } from '../task-files-progress';
import { TaskDrawerSkeleton } from './components';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from './models/GetAccessCurrentUserInQueueTaskByIdResponseModel';
import { ProjectModel } from '@/models';

/**Api сервис */
const middlewareService = new MiddlewareAdapterService();
const router = useRouter();
const accessAdapter = new AccessAdapterService();
const emit = defineEmits<{
  /**Событие успешного создание задачи */
  (e: 'onCreateTask', task: TaskModel): void;
  /**Событие успешного редактирования задачи */
  (e: 'onUpdateTask', task: Partial<ListTaskModel>): void;
  /**Добавление родительской задачи */
  (e: 'subtaskItem', parentTaskId: number, taskId: number);
  /**Открепление подзадачи */
  (e: 'removeSubtaskItem', taskId: number);
  /**Подтверждение удаления */
  (e: 'successRemove', taskId: number);
  /**Возврат на предыдущую шторку */
  (e: 'onBack'): void;
}>();

const props = defineProps({
  /**Класс для панели */
  panelClass: { type: String },
  /**Скрыть кнопку перехода на детальную страницу */
  hideDetailButton: {
    type: Boolean,
    default: false,
  },
  /**Отключить управления событиями */
  disableEvents: {
    type: Boolean,
    default: false,
  },
  /**Показать кнопку возврата */
  showBackButton: {
    type: Boolean,
    default: false,
  },
  /**Показывать кнопку закрытия */
  showCloseButton: {
    type: Boolean,
    default: true,
  },
});

provide('popover-scroll-selector', '.tir-scroll .hide-scroll');

const adapter = new AdapterService();
/**Ссылка на компонент таймера */
const $timeTracker = ref<InstanceType<typeof TaskTimeTrackerPopover>>();
/**Сссылка на компонент */
const $dialogModal = ref<ComponentInstance<typeof TaskDialogModal>>();
/**Доступы юзера к очереди */
const userAccess = ref<GetAccessCurrentUserInQueueTaskByIdResponseModel>();
const editing = ref(true); // TODO: Переделать на считывание из апи
/**Раскрыто ли полностью описание */
const isExpandDescription = ref(false);
/**Предыдущие данные формы */
const prevFormDataJsonString = ref<string>(JSON.stringify(new TaskFormDataModel()));
/**Данные формы */
const formData = ref(new TaskFormDataModel());
/**Информация о редактируемой задаче */
const editTaskInfo = ref<TaskModel>();
/**Режим редактирования */
const isEditMode = computed(() => !!editTaskInfo.value);
/**ID очереди */
const queueId = ref<string>();
/**ID проекта */
const projectId = ref<number>();
/**Является ли пользователь админом для области */
const isNamespaceAdmin = ref(false);
/**Является ли пользователь админом проекта */
const isProjectAdmin = ref(false);
/**Доступы юзера к род. задаче */
const userAccessToParentTask = ref<GetUserRoleByTaskIdsItemModel>();
/**Перенос задачи */
const isTransferTask = ref<boolean>(false);
/**Собираются ли закрывать шторку */
const isClosingDrawer = ref(false);
/**Предыдущее значение описания */
const previousDescription = ref<string>();
/**Параметры роута */
const paramRoute = computed(() => ({
  taskId: editTaskInfo.value?.Id,
  namespaceId: editTaskInfo.value?.NamespaceId,
  queueId: editTaskInfo.value?.QueueTaskId,
  projectId: editTaskInfo.value?.ProjectId,
}));
/**Активный сервис для импорта задачи */
const activeImportService = ref<TaskImportServiceEnum>();

/**Блокировка полей при импорте */
const importDisableFields = computed(
  () => !isEditMode.value && activeImportService.value !== undefined && !formData.value.WeeekTask,
);
/**Были ли сохранены изменения в описании */
const isChangedDescription = computed(
  () => previousDescription.value == formData.value.Description,
);
/**Видимость тега weeek */
const isVisibleWeeekTag = computed(
  () => activeImportService.value === TaskImportServiceEnum.Weeek || !!formData.value.WeeekTask,
);
/**Ссылка на задачу weeek */
const weeekTaskUrl = computed(() =>
  formData.value.WeeekTask?.WeeekTaskId !== undefined &&
  formData.value.WeeekTask?.WeeekWorkspaceId !== undefined
    ? `https://app.weeek.net/ws/${formData.value.WeeekTask.WeeekWorkspaceId}/task/${formData.value.WeeekTask.WeeekTaskId}`
    : undefined,
);

/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'task-drawer',
      life: life ?? 2000,
      closable: true,
    }),
  );
};

/**
 * Обновляет активный источник импорта задачи.
 *
 * Если выбран новый источник импорта (service !== undefined):
 * - При наличии несохранённых изменений запрашивает подтверждение на их перезапись.
 * - После подтверждения сбрасывает форму и устанавливает новый сервис.
 *
 * Если источник импорта сбрасывается (service === undefined):
 * - При наличии импортированных данных запрашивает подтверждение на их удаление.
 * - После подтверждения сбрасывает форму и отключает активный сервис.
 *
 * Метод не выполняется, если открыт режим редактирования или диалоговое окно недоступно.
 */
const updateActiveImportService = async (service: TaskImportServiceEnum | undefined) => {
  // Прерываем выполнение, если модалка не инициализирована или включен режим редактирования
  if (!$dialogModal.value || isEditMode.value) return;

  const resetForm = () => {
    formData.value = new TaskFormDataModel({ Parent: formData.value.Parent });
    setDefaults();
  };

  // Если передан новый сервис
  if (service !== undefined) {
    // Есть несохранённые изменения — спрашиваем подтверждение
    if (isChanged.value) {
      const isConfirmed = await $dialogModal.value.open(TaskDialogEnum.DataWillBeOverwritten);
      if (!isConfirmed) return;

      resetForm();
    }

    // В любом случае после подтверждения или если нет изменений — применяем новый сервис
    activeImportService.value = service;
    editing.value = false;
    return;
  }

  // Если сервис не передан, проверяем, импортированы ли данные
  const hasImportedData = !!formData.value.WeeekTask;

  if (hasImportedData) {
    const isConfirmed = await $dialogModal.value.open(TaskDialogEnum.ImportedDataWillBeReset);
    if (!isConfirmed) return;

    resetForm();
  }

  // Очищаем текущий сервис и включаем режим редактирования
  activeImportService.value = undefined;
  editing.value = true;
};
/**
 * Устанавливает данные задачи для импорта и включает режим редактирования.
 *
 * Этот метод принимает данные задачи для импорта и обновляет форму с новыми значениями.
 * Если для каких-либо полей импорта не указаны значения, используется текущее значение из формы.
 * После этого активируется режим редактирования.
 *
 * @param importData Объект данных для импорта, содержащий поля задачи.
 */
const setImportTask = (importData: TaskImportDataModel) => {
  formData.value = new TaskFormDataModel({
    ...formData.value,
    WeeekTask: new WeeekTaskModel({
      WeeekTaskId: importData.WeeekTaskId,
      WeeekWorkspaceId: importData.WeeekWorkspaceId,
    }),
    Name: importData.Name,
    Description: importData.Description,
    StatusId: importData.StatusId ?? formData.value.StatusId,
    TypeId: importData.TypeId ?? formData.value.TypeId,
    PriorityId: importData.PriorityId ?? formData.value.PriorityId,
    DifficultyId: importData.DifficultyId ?? formData.value.DifficultyId,
    Tags: importData.Tags,
    FilesToImport: importData.Files,
  });
  editing.value = true;
};
/**Открепить родительскую задачу */
const unAttachParent = async () => {
  await unAttachParentTask();
  visible.value = false;
};

const isShowTextChangeTarget = ref(false);
const {
  isChangedSubtasks,
  subtasks,
  isLoadingUnattachParent,
  getSubtasks,
  attachSubtask,
  removeSubtask,
  setParentTask,
  unAttachParentTask,
  searchTasksToAttach,
  searchTasksChangeParent,
} = useTaskSubtasks({
  currentTask: editTaskInfo,
  onSuccessSetParentTask: async (parentTaskId: number) => {
    if (editTaskInfo.value) {
      emit('subtaskItem', parentTaskId, editTaskInfo.value.Id);
      await getTaskDetail(editTaskInfo.value.Id);
    }
  },
  onUnAttachParentTask: async (subtaskId: number) => {
    emit('removeSubtaskItem', subtaskId);
  },
});

const {
  attachFileFromDescription,
  removeFile,
  visibleFiles,
  editorFiles,
  startImportFiles,
  abortFiles,
  isLoadingAbortFiles,
} = useTaskFiles({
  currentTask: editTaskInfo,
  formData: formData,
  queueId: queueId,
  projectId: projectId,
});

/**Списки */
const { dictionaries, getDictionaries } = useTaskDictionaries();

//
/**Управление таймером */
const {
  timerStartDate,
  isLoadingTimerButton,
  timerTrackerRecords,
  startTimer,
  stopTimer,
  createTimerRecord,
  editTimerRecord,
  deleteTimerRecord,
  initTimerData,
} = useTaskTimer({
  taskId: computed(() => editTaskInfo.value?.Id),
  onSuccessCreateTimerRecord: () => $timeTracker.value?.clearInputValue(),
  onSuccessEditTimerRecord: () => $timeTracker.value?.clearInputValue(),
  onOpenConfirmStartTimer: () => $timeTracker.value?.$timeTracker?.$startConfrimPopover?.open(),
});

const route = useRoute();

/**Изменены ли данные в форме */
const isChanged = computed(() => {
  return prevFormDataJsonString.value !== JSON.stringify(formData.value) || isChangedSubtasks.value;
});
const taskOwner = computed(() => {
  if (editTaskInfo.value?.Id) {
    return editTaskInfo.value.Owner;
  } else {
    return { ...CurrentUser, ImageUrl: CurrentUser.Foto };
  }
});

/**Список исполнителей для мультиселекта */
const multiselectPerformers = computed(() =>
  editing.value ? dictionaries.value.Users : formData.value.Performers,
);
/**Список наблюдателей для мультиселекта */
const multiselectObservers = computed(() =>
  editing.value ? dictionaries.value.Users : formData.value.Observers,
);
/**Разрешено редактирование статуса */
const allowStatusChange = computed(() => {
  const isOwner = CurrentUser.Roles.includes(UserRoleEnum.Owner);
  const isObserver = formData.value?.ObserverIds?.includes(CurrentUser.Id);
  const isAuthor = formData.value?.Owner?.Id == CurrentUser.Id;

  return isOwner || isPerformer.value || isObserver || isAuthor || isNamespaceAdmin.value;
});
/**Пользователь является исполнителем задачи */
const isPerformer = computed(() => formData.value?.PerformerIds?.includes(CurrentUser.Id));
/**Отключено ли редактирование статуса */
const statusDisabled = computed(
  () => (!editing.value && !allowStatusChange.value) || importDisableFields.value,
);
/**Отключен учет времени */
const isTimeTrackerDisabled = computed(() => !isEditMode.value);
/**Описание */
const description = computed({
  get: () => formData.value.Description,
  set: (value: string) => {
    if (value === '<div><br></div>') {
      delete formData.value.Description;
    } else {
      formData.value.Description = value;
    }
  },
});

/**Режим инпута */
const inputMode = ref(TaskInputNameModeEnum.Create);
/**Режим поля ввода описания */
const descriptionIsEditMode = ref(!editTaskInfo.value);
const items = ref<NamespaceModel[]>([]);
const taskLocation = ref<QueueTasksModel>();

const isTaskLocation = computed(() => {
  return (
    (!!taskLocation.value && !route.params?.queueId) ||
    (!taskLocation.value && !route.params?.queueId)
  );
});
const isDefineQueueId = computed(() => {
  return !!route.params?.queueId;
});
const isSetQueueId = ref(false);

async function onChangeTaskLocation(value: QueueTasksModel) {
  let column: TasksKanbanColumnModel;
  isShowTextChangeTarget.value = false;
  let tmpName = formData.value.Name;
  let tmpDesk = formData.value.Description;

  queueId.value = value.Id;

  projectId.value = Number(editTaskInfo.value?.ProjectId);
  const groupping = column?.Grouping;
  let req: any = new TaskDrawerSettingsModel({
    Mode: 'create',
    ProjectId: projectId.value,
    QueueId: value.Id,
    NamespaceId: value.NamespaceId,
    FormData: new TaskDrawerFormDataModel({
      StatusId: groupping?.Status?.Id,
      PriorityId: groupping?.Priority?.Id,
      TypeId: groupping?.Type?.Id,
      PerformerIds: groupping?.Performer?.Id ? [groupping.Performer.Id] : [],
      Tags: groupping?.Tag?.Id ? [groupping.Tag.Id] : [],
    }),
  });

  await setCreateTask(req);
  formData.value.Name = tmpName;
  formData.value.Description = tmpDesk;

  isSetQueueId.value = true;
}

async function initItems(projectId: number) {
  const res = await adapter.getTree(projectId, false, true);
  items.value = res.Value;
}

watch(
  () => isEditMode.value,
  async () => {
    if (isEditMode.value) {
      inputMode.value = TaskInputNameModeEnum.View;
    }
  },
);

/**Открыт ли дропдаун поиска задач */
const isOpenedTaskSearchDropdown = ref(false);
/**Стили блока подзадач */
const subtasksElementStyle = computed<CSSProperties>(() => {
  let paddingBottom = 58;
  if (isOpenedTaskSearchDropdown.value) {
    paddingBottom += 273;
    paddingBottom -= 49.33 * subtasks.value.length;
  }
  return { paddingBottom: `${paddingBottom / 16}rem` };
});

const stateSettingsTask = ref();

const showSettingsTask = (value) => {
  stateSettingsTask.value = value;
};

/**Заголовок */
const title = computed(() => {
  if (!isEditMode.value) return formData.value.Parent?.Id ? 'Новая подзадача' : 'Новая задача';
  if (editTaskInfo.value?.DateCreate && editTaskInfo.value?.DateUpdate) {
    const dateCreate = editTaskInfo.value.DateCreate;
    const dateUpdate = editTaskInfo.value.DateUpdate;
    return `Создано ${tirPmDateTransform(dateCreate).format(
      'DD.MM.YYYY',
    )}, обновлено ${tirPmDateTransform(dateUpdate).format('DD.MM.YYYY')} в ${tirPmDateTransform(
      dateUpdate,
    ).format('HH:mm')}`;
  }
});

const { saveFormDataTransfer } = useTaskFormDataTransfer();

/**Проверить является ли пользователь админом области */
const checkNamespaceAdmin = async (namespaceId: string) => {
  const response = await middlewareService.CheckNamespaceAdmin(namespaceId);
  if (response.IsSuccess) isNamespaceAdmin.value = !!response.Value;
};
/**Проверить является ли пользователь админом проекта */
const checkProjectAdmin = async () => {
  const response = await middlewareService.CheckProjectAdmin(projectId.value);
  if (response.IsSuccess) isProjectAdmin.value = !!response.Value;
};
const onAttachSubtask = async (subtaskId: number) => {
  await attachSubtask(subtaskId);
};

const onRemoveSubtask = async (subtaskId: number) => {
  await removeSubtask(subtaskId);
  emit('removeSubtaskItem', subtaskId);
};
/**Открыть детальную страницу */
const taskDetailRoute = computed(() => {
  if (taskLocation.value) {
    return {
      name: 'task-detail',
      params: {
        namespaceId: taskLocation.value?.NamespaceId?.toString(),
        queueId: taskLocation.value?.Id?.toString(),
        taskId: editTaskInfo.value?.Id,
      },
    };
  } else if (editTaskInfo.value) {
    return {
      name: 'task-detail',
      params: {
        projectId: editTaskInfo.value?.ProjectId,
        namespaceId: editTaskInfo.value.NamespaceId,
        queueId: editTaskInfo.value.QueueTaskId,
        taskId: editTaskInfo.value?.Id,
      },
    };
  }
  return '';
});

/**При успешном редактирование */
const onSuccessEdit = () => {
  if (editTaskInfo.value) {
    editTaskInfo.value.DateUpdate = new Date();
  }
};
/**Закрыть шторку */
const closeDrawer = () => {
  visible.value = false;
};
/**При не успешном редактировании */
const onFailEdit = async (taskId: number) => {
  await getTaskDetail(taskId);
};

const updateTaskName = (value: string) => {
  formData.value.Name = value;
  saveTaskName();
};
/**Получить инициалы пользователя */
const getUserInitials = (user: UserModel | CurrentUserModel) => {
  return user.FirstName?.[0]?.toUpperCase() + user.LastName?.[0]?.toUpperCase();
};
/**Сохранить название задачи */
const saveTaskName = async () => {
  if (!formData.value.Name.trim() || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdateNameModel({
    Id: editTaskInfo.value.Id,
    Name: formData.value.Name,
  });
  const resp = await adapter.updateTaskName(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Название задачи обновлено');
    emit('onUpdateTask', { Id: editTaskInfo.value?.Id, Name: formData.value.Name });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении названия задачи', true);
  }
};

/**Сохранить статус задачи */
const saveTaskStatus = async (statusId: number) => {
  formData.value.StatusId = statusId;
  if (statusId === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdateStatusModel({
    Id: editTaskInfo.value.Id,
    StatusId: statusId,
  });
  const resp = await adapter.updateTaskStatus(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Статус задачи обновлен');
    const status = dictionaries.value.Statuses.find((s) => s.Id == statusId);
    emit('onUpdateTask', { Id: editTaskInfo.value?.Id, Status: status });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении статуса задачи', true);
  }
};
/**Сохранить приоритет задачи */
const saveTaskPriority = async (priorityId: number) => {
  formData.value.PriorityId = priorityId;
  if (priorityId === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdatePriorityModel({
    Id: editTaskInfo.value.Id,
    PriorityId: priorityId,
  });
  const resp = await adapter.updateTaskPriority(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Приоритет задачи обновлен');
    const priority = dictionaries.value.Priorities.find((p) => p.Id == priorityId);
    emit('onUpdateTask', { Id: editTaskInfo.value?.Id, Priority: priority });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении приоритета задачи', true);
  }
};
/**Сохранить тип задачи */
const saveTaskType = async (typeId: string) => {
  formData.value.TypeId = typeId;
  if (typeId === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdateTypeModel({
    Id: editTaskInfo.value.Id,
    TaskTypeId: typeId,
  });
  const resp = await adapter.updateTaskType(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Тип задачи обновлен');
    const type = dictionaries.value.Types.find((t) => t.Id == typeId);
    emit('onUpdateTask', {
      Id: editTaskInfo.value?.Id,
      Type: new TaskParameterModel({
        Id: type.Id,
        Color: type.Color,
        Name: type.Name,
      }),
    });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении типа задачи', true);
  }
};
/**Сохранить уровень сложности */
const saveTaskDifficulty = async (difficultyId: string) => {
  formData.value.DifficultyId = difficultyId;
  if (difficultyId === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdateDifficultyModel({
    Id: editTaskInfo.value.Id,
    DifficultyId: difficultyId,
  });
  const resp = await adapter.updateTaskDifficulty(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Сложность задачи обновлена');
    const difficulty = dictionaries.value.Difficulties.find((d) => d.Id == difficultyId);

    emit('onUpdateTask', {
      Id: editTaskInfo.value?.Id,
      Difficulty: new DifficultyLevelTaskModel({
        DifficultyId: difficulty.Id,
        DifficultyName: difficulty.Name,
        DifficultyColor: difficulty.Color,
      }),
    });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении сложности задачи', true);
  }
};
/**Сохранить исполнителей */
const saveTaskPerformers = async (performerIds: Array<number>) => {
  formData.value.PerformerIds = performerIds;
  if (performerIds === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdatePerformersModel({
    Id: editTaskInfo.value.Id,
    PerformerIds: performerIds,
  });
  const resp = await adapter.updateTaskPerformers(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Список исполнителей обновлен');
    const performers = dictionaries.value.Users.filter((u) => performerIds.includes(u.Id));
    emit('onUpdateTask', {
      Id: editTaskInfo.value?.Id,
      Performers: performers.map(
        (o) =>
          new UserModel({
            Id: o.Id,
            FirstName: o.FirstName,
            LastName: o.LastName,
            MiddleName: o.MiddleName,
            Email: o.Email,
            ImageUrl: o.ImageUrl,
            AvatarColor: o.AvatarColor,
          }),
      ),
    });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении списка исполнителей', true);
  }
};
/**Сохранить наблюдателей */
const saveTaskObservers = async (observerIds: Array<number>) => {
  formData.value.ObserverIds = observerIds;
  if (observerIds === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdateObserversModel({
    Id: editTaskInfo.value.Id,
    ObserversId: observerIds,
  });
  const resp = await adapter.updateTaskObservers(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Список наблюдателей обновлен');
    const observers = dictionaries.value.Users.filter((u) => observerIds.includes(u.Id));
    emit('onUpdateTask', {
      Id: editTaskInfo.value?.Id,
      Observers: observers.map(
        (o) =>
          new UserModel({
            Id: o.Id,
            FirstName: o.FirstName,
            LastName: o.LastName,
            MiddleName: o.MiddleName,
            Email: o.Email,
            ImageUrl: o.ImageUrl,
            AvatarColor: o.AvatarColor,
          }),
      ),
    });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении списка наблюдателей', true);
  }
};
/**Сохранить теги */
const saveTaskTags = async (tagIds: Array<number>) => {
  formData.value.Tags = tagIds;
  if (tagIds === undefined || !editTaskInfo.value?.Id) return;
  const request = new TaskUpdateTagsModel({
    Id: editTaskInfo.value.Id,
    Tags: tagIds,
  });
  const resp = await adapter.updateTaskTags(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Теги обновлены');
    const tags = dictionaries.value.Tags.filter((t) => tagIds.includes(t.Id));

    emit('onUpdateTask', { Id: editTaskInfo.value?.Id, Tags: tags });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении тегов', true);
  }
};
/**Сохранить описание */
const saveDescription = async () => {
  if (!editTaskInfo.value?.Id) return;

  descriptionIsEditMode.value = false;
  previousDescription.value = formData.value.Description;

  const request = new TaskUpdateDescriptionModel({
    Id: editTaskInfo.value.Id,
    Description: formData.value?.Description ?? '',
  });

  const resp = await adapter.updateTaskDescription(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Описание задачи обновлено');
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении описания задачи', true);
  }
};
const onClickCancelSaveDescription = async () => {
  if (!isChangedDescription.value) {
    const responce = await $dialogModal.value.open(TaskDialogEnum.CancelDescriptionChanges);
    if (!responce) {
      resetDescriptionEditMode();
    }
    return;
  }
  if (isEditMode.value) {
    resetDescriptionEditMode();
  }
};
/**Сброс режима редактировании описания */
const resetDescriptionEditMode = () => {
  descriptionIsEditMode.value = false;
  formData.value.Description = previousDescription.value;
};
/**Сохранить дату дедлайна */
const saveDeadline = async (deadlineDate: Date) => {
  if (!deadlineDate) return;
  formData.value.DeadlineDate = deadlineDate;
  if (!editTaskInfo.value?.Id) return;
  const request = new TaskUpdateDateEndModel({
    Id: editTaskInfo.value.Id,
    DateEnd: deadlineDate,
  });
  const resp = await adapter.updateDeadline(request);
  if (resp.IsSuccess) {
    onSuccessEdit();
    addNotification('Дедлайн задачи обновлен');
    emit('onUpdateTask', {
      Id: editTaskInfo.value?.Id,
      Deadline: deadlineDate,
    });
  } else {
    onFailEdit(request.Id);
    addNotification('Произошла ошибка при обновлении дедлайна задачи', true);
  }
};

/**Загрузка в кнопке действия */
const isLoadingCreateButton = ref(false);

/**Успешное создание задачи */
const onSuccessCreateTask = async (taskId: number) => {
  addNotification('Задача была создана');
  const isSuccess = await getTaskDetail(taskId);
  if (isSuccess) {
    descriptionIsEditMode.value = false;

    await getSubtasks();
    emit('onCreateTask', editTaskInfo.value);
  }
};

const isTaskSaved = ref(false);

const getErrorMessageCreateTask = (response: ResponseModel<any>) => {
  switch (response.ErrorCode) {
    case 1046:
      return 'Задача уже импортирована';
    default:
      return response.ErrorMessage;
  }
};

/**Создание задачи */
const createTask = async () => {
  if (!queueId) throw new Error('Отсутствует id очереди');
  if (!projectId) throw new Error('Отстствует id проекта');

  const request = new CreateTaskModel({
    ...formData.value,
    QueueId: queueId.value,
    ProjectId: projectId.value,
    CopyTaskId: formData.value.CopyTaskId,
  });
  const resp = await adapter.createTask(request);

  if (resp.IsSuccess) {
    activeImportService.value = undefined;
    await onSuccessCreateTask(resp.Value);
    isTaskSaved.value = true;
  } else {
    addNotification(getErrorMessageCreateTask(resp), true, 5000);
  }
  isTaskSaved.value = false;
};
const transferTaskId = ref();

/**Ошибка при переносе */
const transferTaskError = ref<string>();

/**Перенос задачи */
const getTransferredTaskData = async () => {
  const response = await adapter.getTaskDetail(+route.query.copy);
  transferTaskId.value = response.Value.Id;

  await adapter.getTaskDetail(transferTaskId.value);
};

const { StartValidate, StopValidate, errors, isDisabledSubmit } = useFormValidator(
  computed(() => ({
    Name: formData.value.Name,
  })),
  {
    Name: [FormValidators.required('Название задачи — обязательное поле')],
  },
);
/**Клик на кнопку действия */
const onClickActionButton = async () => {
  if (!StartValidate()) {
    addNotification(
      "Заполните все обязательные для заполнения поля (<span style='color: red; font-size: 1.5rem'>*</span>)",
      false,
      5000,
    );
    return;
  }
  // Создание задачи
  if (!isEditMode.value) {
    isLoadingCreateButton.value = true;
    await startImportFiles();
    await createTask();
    isLoadingCreateButton.value = false;
  }

  StopValidate();
};

/**Установить дефолтные значения */
const setDefaults = () => {
  if (editTaskInfo.value) return;
  const { DefaultStatus, DefaultPriority, DefaultDifficulty, DefaultType } = dictionaries.value;

  formData.value = new TaskFormDataModel({
    ...formData.value,
    StatusId: formData.value.StatusId === undefined ? DefaultStatus?.Id : formData.value.StatusId,
    PriorityId:
      formData.value.PriorityId === undefined ? DefaultPriority?.Id : formData.value.PriorityId,
    DifficultyId:
      formData.value.DifficultyId === undefined
        ? DefaultDifficulty?.Id
        : formData.value.DifficultyId,
    TypeId: formData.value.TypeId === undefined ? DefaultType?.Id : formData.value.TypeId,
  });
};

/**Были ли проинициализированны даты */
let isInitedData = false;
/**Загрузка при инициализации */
const isInitLoading = ref(false);

/**Режим копирования */
const copyMode = ref(false);

/**Видимость tir-pm-drawer */
const visible = ref(false);

/**
 * Открыть шторку
 * @param settings Настройки открытия шторки
 */

const open = async <T extends TaskDrawerMode>(settings: TaskDrawerSettingsModel<T>) => {
  taskLocation.value = null;
  isSetQueueId.value = !!settings.QueueId;

  if (TaskDrawerSettingsTypeGuard.isCreate(settings)) setCreateTask(settings);
  else if (TaskDrawerSettingsTypeGuard.isCreateSubtask(settings)) setCreateSubtask(settings);
  else if (TaskDrawerSettingsTypeGuard.isEdit(settings)) setEditTask(settings);
  else if (TaskDrawerSettingsTypeGuard.isCopy(settings)) setCopyTask(settings);
};
/**Очистить данные */
const clearData = () => {
  editTaskInfo.value = undefined;
  activeImportService.value = undefined;
  subtasks.value = [];
};

/**Включен импорт задач weeek */
const isEnableWeeekImport = ref(false);
/**Проверка включения интеграции проекта weeek */
const checkEnableWeeekImport = async (projectId: number) => {
  const resp = await adapter.checkEnableWeeekImport(projectId);
  if (resp.IsSuccess) {
    isEnableWeeekImport.value = resp.Value;
  }
};

/**Установка режима для создания задачи */
const setCreateTask = async (settings: TaskDrawerSettingsModel<'create'>) => {
  clearData();
  inputMode.value = TaskInputNameModeEnum.Create;
  descriptionIsEditMode.value = true;

  // if (!settings.QueueId) throw new Error("Необходимо передать QueueId");
  if (!settings.ProjectId) throw new Error('Необходимо передать ProjectId');
  initItems(settings.ProjectId);
  isShowTextChangeTarget.value = !settings.QueueId;
  if (queueId.value !== settings.QueueId || projectId.value !== settings.ProjectId) {
    isInitedData = false;
  }

  queueId.value = settings.QueueId;
  projectId.value = settings.ProjectId;
  checkEnableWeeekImport(projectId.value);
  formData.value = new TaskFormDataModel(settings.FormData);
  visible.value = true;
  if (queueId.value) await getDictionaries(queueId.value, projectId.value, settings.NamespaceId);
  setDefaults();
  prevFormDataJsonString.value = JSON.stringify(formData.value);
};
/**Получить доступы юзера к текущей очереди */
const getAccesUser = async () => {
  const result = await accessAdapter.getAccessCurrentUserInQueueTaskById({
    QueueTaskId: editTaskInfo.value.QueueTaskId,
  });
  if (result.IsSuccess) {
    userAccess.value = result.Value;
  }
};
/**Установка режима для создания подзадачи */
const setCreateSubtask = async (settings: TaskDrawerSettingsModel<'create-subtask'>) => {
  if (!settings.ParentTaskId) throw new Error('Необходимо передать ParentTaskId');
  clearData(); // Очищаем данные
  isInitLoading.value = true; // Загрузка в шторке
  visible.value = true; // Видимость шторки
  inputMode.value = TaskInputNameModeEnum.Create; // Режим инпута
  descriptionIsEditMode.value = true; // Режим описания

  // Получить родительскую задачу
  const resp = await adapter.getParentTask(settings.ParentTaskId);
  if (!resp.IsSuccess) return;
  const parentTask = resp.Value;

  const namespaceId = parentTask.NamespaceId;
  if (!namespaceId) return;

  queueId.value = parentTask.QueueId; // Установка очереди
  isSetQueueId.value = true;
  projectId.value = parentTask.ProjectId; // Установка проекта
  formData.value = new TaskFormDataModel({ ...settings.FormData, Parent: parentTask });
  await getDictionaries(queueId.value, projectId.value, namespaceId);
  setDefaults();
  prevFormDataJsonString.value = JSON.stringify(formData.value);
  isInitLoading.value = false;
};
/**Установка режима для редактирования задачи */
const setEditTask = async (settings: TaskDrawerSettingsModel<'edit'>) => {
  if (!settings.TaskId) throw new Error('Необходимо передать TaskId');
  isInitLoading.value = true;
  visible.value = true;
  inputMode.value = TaskInputNameModeEnum.View;
  descriptionIsEditMode.value = false;
  await getTaskDetail(settings.TaskId);
  // initItems(editTaskInfo.value.ProjectId);
  const x = await accessAdapter.getUserAccess({ QueueTaskId: editTaskInfo.value.QueueTaskId });

  const id = CurrentUser.Id;
  const isIAutor = editTaskInfo.value.Owner.Id == id;
  editing.value = Boolean(x.Value) || isIAutor;
  isShowTextChangeTarget.value = !editTaskInfo.value.QueueTaskId;
  isSetQueueId.value = true;
  if (!props.disableEvents) await adapter.subscribeToTaskChange(settings.TaskId);
  if (editTaskInfo.value) {
    await getDictionaries(
      editTaskInfo.value.QueueTaskId,
      editTaskInfo.value.ProjectId,
      editTaskInfo.value.NamespaceId,
    );
    await Promise.all([initTimerData(), getSubtasks()]);
  }
  isInitLoading.value = false;
};
/**Установка режима для копирования задачи */
const setCopyTask = async (settings: TaskDrawerSettingsModel<any>) => {
  if (!settings.TaskId) throw new Error('Необходимо передать Id копируемой задачи');
  editTaskInfo.value = undefined;
  subtasks.value = [];
  inputMode.value = TaskInputNameModeEnum.Create;
  descriptionIsEditMode.value = true;
  copyMode.value = true;
  isTransferTask.value = route.query.isTransferTask == 'true';

  const response = await adapter.getTaskDetail(settings.TaskId);

  const copyTask = response.Value;
  formData.value = new TaskFormDataModel({
    Name: copyTask.Name,
    Description: copyTask.Description,
    CopyTaskId: copyTask.Id,
    Files: copyTask.Files,
  });
  queueId.value = settings.QueueId;
  projectId.value = settings.ProjectId;

  await getDictionaries(queueId.value, projectId.value, settings.NamespaceId);

  // await attachFileFromDescription(copyTask.Files);
  setDefaults();
  prevFormDataJsonString.value = JSON.stringify(formData.value);
  visible.value = true;
};
/**При обновлении видимости */
const onUpdateVisible = async (value: boolean) => {
  isClosingDrawer.value = true;
  if (!isChangedDescription.value) {
    const response = await $dialogModal.value.open(TaskDialogEnum.CancelDescriptionChanges);
    if (!response) {
      closeDrawer();
    }
    return;
  }
  if (!$dialogModal.value) return;

  if (!isEditMode.value && isChanged.value && !isLoadingCreateButton.value) {
    const isConfirm = await $dialogModal.value.open(TaskDialogEnum.ExitWithoutSaving);
    if (isConfirm) {
      onExit();
    }
    return;
  }
  if (!value) {
    StopValidate();
    if (editTaskInfo.value?.Id && !props.disableEvents)
      await adapter.unSubscribeToTaskChange(editTaskInfo.value.Id);
  }
  visible.value = value;
  isClosingDrawer.value = false;
};

/**При выходе */
const onExit = () => {
  visible.value = false;
  StopValidate();
};

/**
 * * Получить делатьную информаци о задаче
 * @param taskId ID задачи
 */
const getTaskDetail = async (taskId: number) => {
  const resp = await adapter.getTaskDetail(taskId);
  if (resp.IsSuccess) {
    projectId.value = resp.Value.ProjectId;

    editTaskInfo.value = resp.Value;

    if (CurrentUser.Roles.includes(UserRoleEnum.Owner)) {
      isNamespaceAdmin.value = true;
      isProjectAdmin.value = true;
    } else {
      await checkNamespaceAdmin(editTaskInfo.value.NamespaceId);
      await checkProjectAdmin();
    }

    formData.value = mapTaskToFormData(resp.Value);
    previousDescription.value = formData.value.Description;
    if (!isInitedData) {
      queueId.value = resp.Value.QueueTaskId;
      projectId.value = resp.Value.ProjectId;
    }
    prevFormDataJsonString.value = JSON.stringify(formData.value);
    if (editTaskInfo.value.Parent?.Id) await getUserAccessToParentTask();
    await getAccesUser();
  }

  return resp.IsSuccess;
};
/**Получить доступы юзера к род. задаче */
const getUserAccessToParentTask = async () => {
  const resp = await adapter.getUserRoleByTaskIdsAsync(
    new GetUserRoleByTaskIdsRequsetModel({
      TaskIds: [editTaskInfo.value.Parent.Id],
    }),
  );
  if (resp.IsSuccess) {
    userAccessToParentTask.value = resp.Value[0];

    editTaskInfo.value.Parent.HasAccessToParentTask =
      userAccessToParentTask.value?.IsAdminNamespace ||
      userAccessToParentTask.value?.IsAdminProject ||
      userAccessToParentTask.value?.IsOwner ||
      userAccessToParentTask.value?.IsPerformer ||
      userAccessToParentTask.value?.IsObserver ||
      userAccessToParentTask.value?.IsAuthor ||
      userAccessToParentTask.value?.VisibilityOtherTask;
  }
};

/**Настройки хлебных крошек */
const breadcrumbsSettings = computed(
  () =>
    new SettingsBreadcrumbsModel({
      ProjectId: projectId.value,
      QueueId: queueId.value,
    }),
);
/**Скопировать id задачи */
const copyId = async () => {
  if (!editTaskInfo.value?.Id) return;
  if (await copyToClipboard(editTaskInfo.value.Id)) {
    addNotification('Идентификатор задачи скопирован');
  }
};
/**Скопировать название задачи */
const copyName = async () => {
  if (!editTaskInfo.value?.Name) return;
  if (await copyToClipboard(editTaskInfo.value.Id + ' ' + editTaskInfo.value.Name)) {
    addNotification('Название задачи скопировано');
  }
};

/**Скопировать название и ссылку */
const copyNameAndLinkTask = async () => {
  if (!editTaskInfo.value?.Id || !projectId.value || !queueId.value) return;

  const route = router.resolve({
    name: 'task-detail',
    params: {
      projectId: projectId.value,
      namespaceId: editTaskInfo.value.NamespaceId,
      queueId: queueId.value,
      taskId: editTaskInfo.value.Id,
    },
  });

  if (!route?.href) return;

  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + route.href;
  const textContent = `${editTaskInfo.value.Id} ${editTaskInfo.value.Name}`;

  if (await copyToClipboard(`[${textContent}](${link})`)) {
    addNotification('Название и ссылка тикета скопирована');
  }
};

/**Скопировать ссылку на задачу */
const copyTaskLink = async () => {
  if (!projectId.value || !queueId.value) return;

  const to = router.resolve({
    name: 'task-detail',
    params: {
      projectId: projectId.value,
      queueId: queueId.value,
      taskId: editTaskInfo.value.Id,
    },
  }).href;

  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + to;
  const textContent = `${editTaskInfo.value.Id}`;
  await copyToClipboard(`[${textContent}](${link})`);
  addNotification('Ссылка на задачу сопирована');
};
/**После удаления задачи */
const onSuccessRemove = (taskId: number) => {
  emit('successRemove', taskId);
  onUpdateVisible(false);
};

defineExpose({
  stateSettingsTask,
  open,
  close: () => onUpdateVisible(false),
  getTransferredTaskData: getTransferredTaskData,
  transferTaskId,
  transferTaskError,
});
</script>

<style lang="scss" scoped>
@import './style/task-drawer.style.scss';
.status-budge {
  color: var(--text-info) !important;
}
.task-drawer__skeleton {
  margin: 1.75rem 1.75rem 1.875rem 1.75rem;
}
</style>
